import type { FC, KeyboardEvent, MouseEvent } from 'react';
import type { Props as ActionsButtonProps } from 'src/components/actions-button/ActionsButton';
import ActionsButton from 'src/components/actions-button/ActionsButton';

import {
  SMART_COPY_EVENTS,
  useSmartCopyAmplitude,
} from './useSmartCopyAmplitude';

type Props = ActionsButtonProps & {
  controlId: string;
};

const isFromMenu = (target: EventTarget | null): boolean =>
  !!(target as HTMLElement | null)?.closest('[role="menu"]');

/**
 * Test-Result-specific wrapper around the shared ActionsButton that fires
 * `Control Test Dropdown Opened Smart Copy Visible` when the user opens the
 * dropdown — via mouse (mousedown) or keyboard (Enter / Space / ArrowDown).
 *
 * ActionsButton's API ("lacks an onOpen callback") and the rendered menu
 * ("expanded items render in the same React subtree so their events bubble
 * through the capture handlers — filter them out")
 *
 * Lives next to `useSmartCopyAmplitude` and `PerformanceRatingRegister`
 * intentionally — the capture mechanics + the analytics they serve are a
 * single feature concern and shouldn't leak into the shared ActionsButton.
 */
const TestResultActionsButton: FC<Props> = ({ controlId, ...rest }) => {
  const logSmartCopyEvent = useSmartCopyAmplitude();

  const handleOpen = () => {
    logSmartCopyEvent(SMART_COPY_EVENTS.DROPDOWN_OPENED, {
      controlId,
    });
  };

  const handleMouseDownCapture = (event: MouseEvent<HTMLSpanElement>) => {
    if (isFromMenu(event.target)) {
      return;
    }
    handleOpen();
  };

  const handleKeyDownCapture = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (
      event.key !== 'Enter' &&
      event.key !== ' ' &&
      event.key !== 'ArrowDown'
    ) {
      return;
    }
    if (isFromMenu(event.target)) {
      return;
    }
    handleOpen();
  };

  return (
    <span
      onMouseDownCapture={handleMouseDownCapture}
      onKeyDownCapture={handleKeyDownCapture}
    >
      <ActionsButton {...rest} />
    </span>
  );
};

export default TestResultActionsButton;
